import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

export const colorModes = ['light', 'dark'] as const;

export type ColorMode = (typeof colorModes)[number];

export const colorModeStorageKey = 'colorMode';

const isColorMode = (value: string | null): value is ColorMode => {
  return value === 'light' || value === 'dark';
};

const readStoredColorMode = (): ColorMode => {
  try {
    const stored = localStorage.getItem(colorModeStorageKey);
    if (isColorMode(stored)) return stored;
  } catch {
    // Storage can be blocked. The app still starts in dark mode.
  }
  return 'dark';
};

const nextColorMode = (mode: ColorMode): ColorMode => {
  switch (mode) {
    case 'dark':
      return 'light';
    case 'light':
      return 'dark';
    default: {
      const unexpected: never = mode;
      return unexpected;
    }
  }
};

interface ColorModeState {
  mode: ColorMode;
}

const initialState: ColorModeState = {
  mode: readStoredColorMode(),
};

const colorModeSlice = createSlice({
  name: 'colorMode',
  initialState,
  reducers: {
    setColorMode(state, action: PayloadAction<ColorMode>) {
      state.mode = action.payload;
    },
    toggleColorMode(state) {
      state.mode = nextColorMode(state.mode);
    },
  },
});

export const { setColorMode, toggleColorMode } = colorModeSlice.actions;

export default colorModeSlice.reducer;
